import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { DataTable } from '../../components/shared/DataTable';
import { MdSearch } from 'react-icons/md';
import { ClearFiltersButton, PageSkeleton } from '@euroasiann/shared/ui';
import { cn } from '../../lib/utils';
import { authenticatedFetch } from '../../lib/api';

interface Vendor {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName?: string;
  phone?: string;
  organizationId?: string;
  organizationName?: string;
  metadata?: Record<string, any>;
  role?: string;
  isActive: boolean;
  onboardingStatus?: 'pending' | 'completed' | 'approved' | 'rejected';
  invitationStatus?: 'pending' | 'accepted' | 'declined' | null;
  vendorCategory?: 'global' | 'contracted';
  vendorCategoryLabel?: string;
  invitedByCustomerName?: string | null;
  lastLogin?: string;
  createdAt?: string;
}

export function VendorsPage() {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState('all');
  const [vendorTypeFilter, setVendorTypeFilter] = useState<'all' | 'global' | 'contracted'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch all vendor-portal users from admin endpoint
  const { data: vendorsData, isLoading } = useQuery<Vendor[]>({
    queryKey: ['admin-vendors', vendorTypeFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (vendorTypeFilter !== 'all') {
        params.set('vendorScope', vendorTypeFilter);
      }
      const query = params.toString();
      const r = await authenticatedFetch(`/api/v1/admin/vendors${query ? `?${query}` : ''}`);
      const json = await r.json();
      const vendors = Array.isArray(json.data) ? json.data : [];
      return vendors.map((vendor: any) => ({
        _id: vendor._id,
        email: vendor.email || '',
        firstName: vendor.firstName || '',
        lastName: vendor.lastName || '',
        fullName: vendor.fullName || vendor.name || `${vendor.firstName || ''} ${vendor.lastName || ''}`.trim(),
        phone: vendor.phone || '',
        organizationId: vendor.organizationId || vendor._id,
        organizationName: vendor.organizationName || '',
        metadata: vendor.metadata || {},
        role: vendor.role,
        isActive: typeof vendor.isActive === 'boolean' ? vendor.isActive : true,
        onboardingStatus: vendor.onboardingStatus,
        invitationStatus: vendor.invitationStatus ?? null,
        vendorCategory: vendor.vendorCategory,
        vendorCategoryLabel: vendor.vendorCategoryLabel,
        invitedByCustomerName: vendor.invitedByCustomerName,
        lastLogin: vendor.lastLogin,
        createdAt: vendor.createdAt,
      }));
    },
    staleTime: 30000,
    retry: 1,
  });

  // Client-side filtering
  const filteredVendors = useMemo(() => {
    if (!vendorsData) return [];
    let filtered = vendorsData;

    if (activeFilter !== 'all') {
      filtered = filtered.filter((vendor) => {
        let status = vendor.onboardingStatus || (vendor.isActive ? 'approved' : 'pending');
        if (status === 'completed') status = 'pending';
        return status === activeFilter;
      });
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (vendor) =>
          vendor.email?.toLowerCase().includes(query) ||
          vendor.firstName?.toLowerCase().includes(query) ||
          vendor.lastName?.toLowerCase().includes(query) ||
          vendor.fullName?.toLowerCase().includes(query) ||
          vendor.organizationName?.toLowerCase().includes(query) ||
          vendor.phone?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [vendorsData, searchQuery, activeFilter]);

  const columns = [
    {
      key: 'name',
      header: 'Vendor Name',
      render: (vendor: Vendor) => (
        <div className="font-semibold text-[hsl(var(--foreground))]">
          {vendor.fullName ||
            `${vendor.firstName || ''} ${vendor.lastName || ''}`.trim() ||
            vendor.organizationName ||
            'N/A'}
        </div>
      ),
    },
    {
      key: 'organizationName',
      header: 'Company',
      render: (vendor: Vendor) => (
        <span className="text-[hsl(var(--muted-foreground))]">{vendor.organizationName || 'N/A'}</span>
      ),
    },
    {
      key: 'email',
      header: 'Email',
      render: (vendor: Vendor) => (
        <span className="text-[hsl(var(--muted-foreground))]">{vendor.email || 'N/A'}</span>
      ),
    },
    {
      key: 'phone',
      header: 'Phone',
      render: (vendor: Vendor) => (
        <span className="text-[hsl(var(--muted-foreground))]">{vendor.phone || 'N/A'}</span>
      ),
    },
    {
      key: 'details',
      header: 'Details',
      render: (vendor: Vendor) => {
        const summary =
          vendor.metadata?.summary ||
          vendor.metadata?.description ||
          vendor.metadata?.businessType ||
          'Marine & offshore supplies';
        return <span className="text-[hsl(var(--muted-foreground))]">{summary}</span>;
      },
    },
    {
      key: 'catalog',
      header: 'Catalog Parts',
      render: (vendor: Vendor) => {
        const catalogParts: string[] = Array.isArray(vendor.metadata?.catalogParts)
          ? vendor.metadata.catalogParts
          : [];
        const fallback = ['Engine Spares', 'Deck Equipment', 'Safety Gear'];
        const display = catalogParts.length > 0 ? catalogParts : fallback;
        return (
          <div className="flex flex-wrap gap-2">
            {display.slice(0, 3).map((part, index) => (
              <span
                key={`${part}-${index}`}
                className="px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-[#0077b3] dark:bg-blue-900/40 dark:text-blue-300"
              >
                {part}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      key: 'vendorCategory',
      header: 'Vendor Type',
      render: (vendor: Vendor) => {
        const isGlobal = vendor.vendorCategory !== 'contracted';
        return (
          <div className="space-y-1">
            <span
              className={cn(
                'px-3 py-1 inline-flex text-xs leading-5 font-bold rounded-full',
                isGlobal
                  ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 ring-1 ring-indigo-200 dark:ring-indigo-800'
                  : 'bg-teal-100 text-teal-800 dark:bg-teal-900/50 ring-1 ring-teal-200 dark:ring-teal-800'
              )}
            >
              {vendor.vendorCategoryLabel || (isGlobal ? 'Global Vendor' : 'Contracted Vendor')}
            </span>
            {vendor.invitedByCustomerName && (
              <p className="text-xs text-[hsl(var(--muted-foreground))]">
                via {vendor.invitedByCustomerName}
              </p>
            )}
          </div>
        );
      },
    },
    {
      key: 'onboardingStatus',
      header: 'Status',
      render: (vendor: Vendor) => {
        let status = 'pending';
        let label = 'Pending';

        if (vendor.invitationStatus) {
          status = vendor.invitationStatus;
          if (status === 'accepted') label = 'Accepted';
          else if (status === 'declined') label = 'Declined';
          else label = 'Invitation Pending';
        } else {
          status = vendor.onboardingStatus || (vendor.isActive ? 'approved' : 'pending');
          if (status === 'completed') status = 'pending';
        }

        const statusConfig: Record<string, { label: string; className: string }> = {
          pending: {
            label: label === 'Invitation Pending' ? 'Invitation Pending' : 'Pending',
            className:
              'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 ring-1 ring-yellow-200 dark:ring-yellow-800',
          },
          accepted: {
            label: 'Accepted',
            className:
              'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 ring-1 ring-emerald-200 dark:ring-emerald-800',
          },
          declined: {
            label: 'Declined',
            className: 'bg-red-100 text-red-800 dark:bg-red-900/50 ring-1 ring-red-200 dark:ring-red-800',
          },
          approved: {
            label: 'Approved',
            className:
              'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 ring-1 ring-emerald-200 dark:ring-emerald-800',
          },
          rejected: {
            label: 'Rejected',
            className: 'bg-red-100 text-red-800 dark:bg-red-900/50 ring-1 ring-red-200 dark:ring-red-800',
          },
        };

        const config = statusConfig[status] || statusConfig.pending;

        return (
          <span
            className={cn(
              'px-3 py-1 inline-flex text-xs leading-5 font-bold rounded-full',
              config.className
            )}
          >
            {config.label}
          </span>
        );
      },
    },
  ];

  return (
    <div className="w-full space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-[hsl(var(--foreground))]">Manage Vendors & Suppliers</h1>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-[hsl(var(--foreground))]">Vendor Type</label>
          <select
            value={vendorTypeFilter}
            onChange={(e) => setVendorTypeFilter(e.target.value as 'all' | 'global' | 'contracted')}
            className="px-3 py-2 bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-lg text-sm text-[hsl(var(--foreground))]"
          >
            <option value="all">All Types</option>
            <option value="global">Global Vendors</option>
            <option value="contracted">Contracted Vendors</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-[hsl(var(--foreground))]">Filter by Status</label>
          <select
            value={activeFilter}
            onChange={(e) => setActiveFilter(e.target.value)}
            className="px-3 py-2 bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-lg text-sm text-[hsl(var(--foreground))]"
          >
            <option value="all">All Statuses</option>
            <option value="approved">Approved</option>
            <option value="pending">Pending</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
        <div className="flex items-center gap-2 flex-1">
          <label className="text-sm font-medium text-[hsl(var(--foreground))]">Search Vendors</label>
          <div className="relative flex-1">
            <MdSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#0088cc]/65 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by vendor name, email, company, phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-lg text-sm text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:outline-none focus:ring-2 focus:ring-[#0088cc] focus:border-transparent"
            />
          </div>
        </div>
        <ClearFiltersButton
          visible={!!searchQuery.trim() || activeFilter !== 'all' || vendorTypeFilter !== 'all'}
          onClick={() => {
            setSearchQuery('');
            setActiveFilter('all');
            setVendorTypeFilter('all');
          }}
          variant="toolbar"
        />
      </div>

      {/* Table */}
      {isLoading ? (
        <PageSkeleton variant="table" />
      ) : (
        <div className="bg-[hsl(var(--card))] rounded-lg border border-[hsl(var(--border))] overflow-hidden">
          <DataTable
            columns={columns}
            data={filteredVendors}
            emptyMessage="No vendors found."
            onRowClick={(vendor: Vendor) => navigate(`/dashboard/admin/vendors/${vendor._id}`)}
          />
        </div>
      )}
    </div>
  );
}


